/**
 * Standardized approach for marking temporary entities in comboboxes/selects
 * that allow inline creation before actual persistence.
 *
 * Usage:
 * 1. Add the marker to your temporary entity
 * 2. Check with isTemporaryEntity() before persisting
 * 3. Use getTemporaryEntityMarker() to access the unique symbol
 */

// Unique symbol that can't be accidentally duplicated
const TEMPORARY_ENTITY_MARKER = Symbol('temporary_entity');

type WithTemporaryMarker = {
	[TEMPORARY_ENTITY_MARKER]?: boolean;
};

/**
 * Type guard to check if an entity is temporary (not yet persisted)
 */
export function isTemporaryEntity<T>(entity: T | null | undefined): boolean {
	if (!entity || typeof entity !== 'object') {
		return false;
	}
	return (entity as unknown as WithTemporaryMarker)[TEMPORARY_ENTITY_MARKER] === true;
}

/**
 * Get the temporary entity marker symbol for adding to entities
 */
export function getTemporaryEntityMarker(): typeof TEMPORARY_ENTITY_MARKER {
	return TEMPORARY_ENTITY_MARKER;
}

/**
 * Mark an entity as temporary
 */
export function markAsTemporary<T extends Record<string, unknown>>(
	entity: T,
): T & WithTemporaryMarker {
	return {
		...entity,
		[TEMPORARY_ENTITY_MARKER]: true,
	};
}
